import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, LoadingBlock } from "@/components/Common";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Languages, Send, Plus, MessageSquare } from "lucide-react";
import { toast } from "sonner";

export default function Concierge() {
    const [sessions, setSessions] = useState([]);
    const [active, setActive] = useState(null);
    const [thread, setThread] = useState(null);
    const [text, setText] = useState("");
    const [guests, setGuests] = useState([]);
    const [guestId, setGuestId] = useState("");
    const [sending, setSending] = useState(false);
    const endRef = useRef(null);

    const loadSessions = async () => {
        const { data } = await api.get("/concierge/sessions");
        setSessions(data);
        if (data.length && !active) setActive(data[0]);
    };

    useEffect(() => {
        loadSessions();
        api.get("/guests").then((r) => setGuests(r.data));
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (!active) return;
        api.get(`/concierge/sessions/${active.id}`).then((r) => setThread(r.data));
    }, [active]);

    useEffect(() => {
        endRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [thread]);

    const startSession = async () => {
        const g = guests.find((x) => x.id === guestId);
        const { data } = await api.post("/concierge/sessions", {
            guest_id: g?.id,
            guest_name: g?.name,
            channel: "web",
        });
        toast.success("Session created");
        await loadSessions();
        setActive(data);
    };

    const send = async () => {
        if (!text.trim() || !active) return;
        setSending(true);
        try {
            await api.post("/concierge/message", { session_id: active.id, text });
            setText("");
            const { data } = await api.get(`/concierge/sessions/${active.id}`);
            setThread(data);
            loadSessions();
        } catch (e) {
            toast.error("Send failed");
        } finally {
            setSending(false);
        }
    };

    return (
        <>
            <PageHeader
                eyebrow="Multilingual · hotel-aware"
                title="AI Concierge"
                subtitle="Guests message in any language; responses use guest preferences and stay context automatically."
            />
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                <Card className="lg:col-span-3">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-base flex items-center gap-2">
                            <MessageSquare className="w-4 h-4" /> Sessions
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="mb-3 space-y-2">
                            <Select value={guestId} onValueChange={setGuestId}>
                                <SelectTrigger data-testid="concierge-guest-select">
                                    <SelectValue placeholder="Choose guest" />
                                </SelectTrigger>
                                <SelectContent>
                                    {guests.map((g) => (
                                        <SelectItem key={g.id} value={g.id}>
                                            {g.name} · {g.language.toUpperCase()}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <Button onClick={startSession} className="w-full" data-testid="concierge-new-session">
                                <Plus className="w-4 h-4 mr-1" /> Start new session
                            </Button>
                        </div>
                        <div className="space-y-1 max-h-[55vh] overflow-y-auto">
                            {sessions.length === 0 && (
                                <div className="text-xs text-muted-foreground">No sessions yet.</div>
                            )}
                            {sessions.map((s) => (
                                <button
                                    key={s.id}
                                    onClick={() => setActive(s)}
                                    className={`w-full text-left rounded-lg px-3 py-2 text-sm border ${active?.id === s.id ? "bg-secondary border-border" : "border-transparent hover:bg-secondary/50"}`}
                                    data-testid="concierge-session"
                                >
                                    <div className="font-medium">{s.guest_name}</div>
                                    <div className="text-xs text-muted-foreground">{s.channel} · {s.language?.toUpperCase()}</div>
                                </button>
                            ))}
                        </div>
                    </CardContent>
                </Card>

                <Card className="lg:col-span-9">
                    <CardHeader className="pb-2 flex flex-row items-center justify-between">
                        <CardTitle className="text-base">
                            {active ? `Chat with ${active.guest_name}` : "Select a session"}
                        </CardTitle>
                        {active && (
                            <span
                                data-testid="chat-language-badge"
                                className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-2 py-1 text-xs font-medium"
                            >
                                <Languages className="w-3 h-3" /> {(active.language || "en").toUpperCase()}
                            </span>
                        )}
                    </CardHeader>
                    <CardContent>
                        {!thread ? (
                            <LoadingBlock rows={5} />
                        ) : (
                            <>
                                <div className="max-h-[55vh] min-h-[40vh] overflow-y-auto space-y-3 pr-1" data-testid="concierge-thread">
                                    {thread.messages.length === 0 && (
                                        <div className="text-sm text-muted-foreground">
                                            Say hello in any language to start the conversation.
                                        </div>
                                    )}
                                    {thread.messages.map((m) => (
                                        <div
                                            key={m.id}
                                            className={`flex ${m.role === "ai" ? "justify-start" : "justify-end"}`}
                                        >
                                            <div
                                                className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${m.role === "ai" ? "border border-border bg-card" : "bg-[hsl(var(--primary)/0.1)]"}`}
                                            >
                                                <div className="whitespace-pre-wrap">{m.text}</div>
                                                <div className="text-[10px] text-muted-foreground mt-1 uppercase tracking-wider">
                                                    {m.role} · {m.language?.toUpperCase()}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                    <div ref={endRef} />
                                </div>
                                {active && (
                                    <div className="mt-3 flex gap-2">
                                        <Textarea
                                            placeholder="Type a message in any language…"
                                            value={text}
                                            onChange={(e) => setText(e.target.value)}
                                            rows={2}
                                            data-testid="chat-composer-input"
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter" && !e.shiftKey) {
                                                    e.preventDefault();
                                                    send();
                                                }
                                            }}
                                        />
                                        <Button onClick={send} disabled={sending || !text.trim()} data-testid="chat-composer-send">
                                            <Send className="w-4 h-4 mr-1" /> {sending ? "Sending" : "Send"}
                                        </Button>
                                    </div>
                                )}
                            </>
                        )}
                    </CardContent>
                </Card>
            </div>
        </>
    );
}
